// Direct browser → Anthropic. User-supplied key stored in localStorage.
const KEY_STORE = "pmv5_apikey";
const API_URL   = "https://api.anthropic.com/v1/messages";
const MODEL     = "claude-sonnet-4-20250514";

export function getApiKey() { try { return localStorage.getItem(KEY_STORE) || ""; } catch (_) { return ""; } }
export function setApiKey(k){ try { localStorage.setItem(KEY_STORE, k || ""); } catch (_) {} }

export async function callClaude(prompt) {
  const key = getApiKey();
  if (!key) throw new Error("Add your Anthropic API key in the bottom strip to enable AI features.");
  const res = await fetch(API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": key,
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({ model: MODEL, max_tokens: 1024, messages: [{ role: "user", content: prompt }] }),
  });
  const d = await res.json();
  if (!res.ok || d.error) throw new Error(d.error?.message || `HTTP ${res.status}`);
  return d.content?.map(c => c.text || "").join("") || "(empty response)";
}

const fK = n => "$" + (Math.round(n / 100) / 10).toFixed(1) + "K";

// Prompt builders
export const prompts = {
  execUpdate: (S) => `You are a Project Manager writing a C-suite weekly status update for "${S.meta.name}". Write 200 words max in this structure: Overall Status (RAG), Key Accomplishments, Issues & Risks, Upcoming Milestones, Decisions Needed.
Phase: ${S.meta.phase}, Health: ${S.meta.health}
Open Issues: ${S.raids.filter(r=>r.type==='Issue'&&r.status!=='Closed').map(r=>r.title).join('; ')}
Critical Path: ${S.tasks.filter(t=>t.critical).map(t=>t.name).join(', ')}
Upcoming Milestones: ${S.milestones.filter(m=>m.status!=='Completed').map(m=>m.name+' ('+m.date+')').join('; ')}`,

  riskSummary: (S) => `You are a senior PM consultant. Analyze this RAID log for "${S.meta.name}" and provide: top 3 risk concerns, the most critical open issue, and 3 actionable recommendations. Max 180 words.

${S.raids.map(r=>`[${r.type}] ${r.ref}: ${r.title} — Status: ${r.status}, Impact: ${r.impact}`).join('\n')}`,

  reqAnalysis: (S) => `You are a business analyst. Review these requirements for "${S.meta.name}". Identify: gaps in coverage, 2 key delivery risks, and which requirements are most at risk. Max 180 words.

${S.requirements.map(r=>`[${r.priority}] ${r.id}: ${r.title} — ${r.status}`).join('\n')}`,

  actionDigest: (S) => `You are a project coordinator. Write a crisp weekly action digest for "${S.meta.name}": highlight overdue items, top 3 critical priorities this week, and one key message for the team. Max 180 words.

${S.actions.map(a=>`[${a.priority}] ${a.id}: ${a.title} — ${a.assignee}, ${a.status}, Due: ${a.due}`).join('\n')}`,

  budgetNarrative: (S) => {
    const ta = S.budget.reduce((s,b)=>s+b.allocated,0);
    const ts = S.budget.reduce((s,b)=>s+b.actual,0);
    const tf = S.budget.reduce((s,b)=>s+b.forecast,0);
    return `You are a financial analyst. Write a concise budget narrative (max 180 words) for "${S.meta.name}": overall spend health, categories needing attention, and forecast risk.
Total Allocated: ${fK(ta)}, Spent: ${fK(ts)}, Forecast: ${fK(tf)}

${S.budget.map(b=>`${b.category}: Budget ${fK(b.allocated)}, Actual ${fK(b.actual)}, Forecast ${fK(b.forecast)}`).join('\n')}`;
  },

  gapAnalysis: (S) => `You are a business systems analyst. Analyze this gap/fit assessment for "${S.meta.name}" and provide: (1) the 3 highest-risk gaps that could delay delivery, (2) any patterns across gaps (e.g. systemic infrastructure issues), (3) recommended sequencing to close gaps. Max 220 words.

Gaps:
${S.gaps.map(g=>`[${g.priority}] ${g.area}: ${g.gap} — Fit: ${g.fitStatus}, Status: ${g.status}, Impact: ${g.impact}`).join('\n')}`,

  s4hanaBrief: (S, s4) => `You are a Senior Director writing a board-ready S/4HANA transformation brief for "${S.meta.name}". Produce ONE paragraph (max 180 words), executive-grade tone, no bullets. Open with overall program posture, weave in 2-3 concrete metrics, name the top systemic risk, name the top value lever already realized, and close with the single decision the steering committee must take next.

Program posture: ${s4.summary.map(x => `${x.label}=${x.value} (${x.trend})`).join('; ')}
Top workstreams behind plan: ${s4.workstreams.filter(w => w.pct < 60).map(w => `${w.name} ${w.pct}%`).join('; ') || 'none'}
Top risks: ${s4.topRisks.slice(0, 4).map(r => `${r.desc} [${r.severity}]`).join('; ')}
Value realization: ${s4.valueKpis.map(v => `${v.label}=${v.value} (${v.sub})`).join('; ')}
Decision velocity (avg days last 5 months): ${s4.decisionTrend.map(d => `${d.m} ${d.d}d`).join(', ')}
Region readiness: ${s4.readiness.map(r => `${r.region} ${r.pct}%`).join(', ')}
Governance signal: ${s4.governance.filter(g => g.status === 'At Risk' || g.status === 'Watch').map(g => `${g.ind} (${g.status})`).join('; ') || 'all stable'}`,
};
